// Review-surface consolidation §7 — one-time onboarding panel shown on the
// portal landing (Requests) tab the first time a user visits. Replaces the
// old per-tab "Welcome" banner that bled across every tab (Round-3 #1
// deleted that). Renders only when the /me response flags isFirstLogin, and
// acknowledges itself on mount via POST /auth/me/first-login-seen so it
// never returns.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { usePortalAuth } from "../PortalAuthContext";
import { portalAuthApi } from "../api/auth";
import { portalPackagesApi } from "../api/packages";
import { isRecipientActionable } from "../lib/completion";
// Imported from `@/design-system/lib/format`, NOT `@/utils/datetime`: only the
// design-system module is on the portal import allow-list (see
// portal/isolation.test.ts), and `@/utils/datetime` carries the staff auth
// hooks. The predicate itself is pure either way.
import { isWithinCalendarDays } from "@/design-system/lib/format";
import type { PortalItemRowDto } from "../api/types";

/** "Due soon" horizon, in whole calendar days — inclusive of today. */
const DUE_SOON_DAYS = 7;

export function FirstLoginWelcomePanel() {
  const { me, setMe } = usePortalAuth();
  const [dismissed, setDismissed] = useState(false);
  const [items, setItems] = useState<PortalItemRowDto[]>([]);

  const show = Boolean(me?.isFirstLogin) && !dismissed;

  // Acknowledge on mount so the panel is genuinely one-time: a second
  // session reads isFirstLogin === false. Fire-and-forget; failures just
  // mean it may show once more, which is harmless.
  useEffect(() => {
    if (!me?.isFirstLogin) return;
    void portalAuthApi.markFirstLoginSeen().catch(() => {});
  }, [me?.isFirstLogin]);

  // Pull the outstanding-request bucket for the live counts.
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    void portalPackagesApi
      .listMyItems("requests")
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [show]);

  if (!show || !me) return null;

  const firstName = me.fullName.trim().split(/\s+/)[0] || me.fullName;
  // "Waiting" = items the recipient still needs to act on; an item that's
  // already under review with staff is not something they're waiting to do.
  const waiting = items.filter(isRecipientActionable);
  // Whole calendar days, INCLUSIVE of today. The shape this replaces compared
  // instants (`due >= Date.now()`), and a `dueDate` is a wall-calendar value
  // stored at UTC midnight — so from 17:00 Phoenix the item due TODAY was
  // already "in the past" and dropped out of the count entirely. The one item
  // most needing attention was the one this number could not see.
  //
  // No `tz` argument, deliberately: the portal renders outside AuthProvider
  // (main.tsx) and has no per-viewer timezone, so this resolves to the app
  // zone — exactly what every other date on this surface already shows. Do NOT
  // reach for the staff `useUserTimeZone` hook here; it is off the allow-list
  // and would read a context the portal never mounts.
  const dueSoon = waiting.filter((i) =>
    isWithinCalendarDays(i.dueDate, DUE_SOON_DAYS),
  ).length;

  return (
    <section
      data-testid="first-login-welcome"
      aria-label="Welcome"
      className="relative mb-space-5 rounded-md border border-border-light bg-white-pure p-space-5"
    >
      <button
        type="button"
        onClick={() => {
          setDismissed(true);
          setMe(me ? { ...me, isFirstLogin: false } : me);
        }}
        aria-label="Dismiss welcome"
        className="absolute right-space-3 top-space-3 rounded-full p-space-1 text-gray-mid hover:bg-white-warm hover:text-onyx"
      >
        <X size={16} aria-hidden />
      </button>
      <h2 className="font-display text-h3 font-bold text-onyx m-0">
        Welcome, {firstName}.
      </h2>
      <p className="text-body text-gray-mid mt-space-2 max-w-reading">
        Phoenix Capital uses this secure portal to collect documents and
        signatures from you.
      </p>
      <p className="text-body text-onyx mt-space-3">
        You have{" "}
        <Link to="?tab=requests" className="font-semibold text-onyx hover:text-gold-shadow">
          {waiting.length} document request{waiting.length === 1 ? "" : "s"} waiting
        </Link>
        {dueSoon > 0 ? `, ${dueSoon} due in the next 7 days.` : "."}
      </p>
      <p className="text-small text-gray-mid mt-space-2 max-w-reading">
        Documents you upload appear in <strong>Documents</strong>; reference
        materials we've published are in <strong>Files &amp; library</strong>.
      </p>
    </section>
  );
}
